import { useEffect, useState } from 'react';
import { useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { breakpoints, componentConfig, themeBreakpoints } from './componentConfig';
import { darkColors, lightColors } from './colors';

// Hook to get current responsive theme configuration
export function useSimpleResponsiveTheme() {
	// Use theme-specific breakpoints for component props
	const isTablet = useMediaQuery(`(min-width: ${themeBreakpoints.mobile}px)`);
	const isDesktop = useMediaQuery(`(min-width: ${themeBreakpoints.tablet}px)`);

	// Determine current breakpoint
	let currentBreakpoint: 'desktop' | 'tablet' | 'mobile' = 'mobile';
	if (isDesktop) {
		currentBreakpoint = 'desktop';
	} else if (isTablet) {
		currentBreakpoint = 'tablet';
	}

	// Get the appropriate configuration
	const config = componentConfig[currentBreakpoint];

	return {
		breakpoint: currentBreakpoint,
		theme: config,
		sizes: {
			defaultRadius: config.defaultRadius,
			components: config.components,
		},
	};
}

// Hook to check if dark mode is active
export function useIsDarkMode() {
	const { colorScheme } = useMantineColorScheme();
	return colorScheme === 'dark';
}

// Hook to get semantic colors based on current color scheme
export function useThemeColors() {
	const isDark = useIsDarkMode();
	return isDark ? darkColors : lightColors;
}

// Hook to get current breakpoint
export function useBreakpoint() {
	const isTablet = useMediaQuery(`(min-width: ${breakpoints.mobile}px)`);
	const isDesktop = useMediaQuery(`(min-width: ${breakpoints.tablet}px)`);

	if (isDesktop) return 'desktop';
	if (isTablet) return 'tablet';
	return 'mobile';
}

// Hook to track screen size changes
export function useScreenSize() {
	const [screenSize, setScreenSize] = useState({
		width: typeof window !== 'undefined' ? window.innerWidth : 0,
		height: typeof window !== 'undefined' ? window.innerHeight : 0,
	});

	useEffect(() => {
		const handleResize = () => {
			setScreenSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return screenSize;
}

// Hook to get a specific color from the theme
export function useThemeColor(color: string, shade?: number) {
	const theme = useMantineTheme();

	if (shade !== undefined) {
		return theme.colors[color]?.[shade] || color;
	}

	// Return primary shade if no shade specified
	const { primaryShade } = theme;
	const defaultShade = typeof primaryShade === 'object' ? primaryShade.light : primaryShade;
	return theme.colors[color]?.[defaultShade] || color;
}

// Hook to apply responsive styles
export function useResponsiveStyle<T extends Record<string, unknown>>(styles: {
	mobile?: T;
	tablet?: T;
	desktop?: T;
}) {
	const breakpoint = useBreakpoint();

	// Merge styles based on breakpoint hierarchy
	const baseStyles = styles.mobile || ({} as T);
	const tabletStyles = breakpoint !== 'mobile' ? styles.tablet || {} : {};
	const desktopStyles = breakpoint === 'desktop' ? styles.desktop || {} : {};

	return {
		...baseStyles,
		...tabletStyles,
		...desktopStyles,
	} as T;
}
